import {
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  resetPassword,
  signIn,
  signOut,
  type SignInOutput,
} from 'aws-amplify/auth';
import { Router } from 'vue-router';

import { UserModule } from '@/store/user.module';

export interface UserPrincipal {
  username: string;
  groups: string[];
  sub: string;
  email: string;
  name: string;
  familyName: string;
  clientId: string;
  cguAcceptance?: string;
}

export default class CognitoAuth {
  static async signInUser(username: string, password: string): Promise<SignInOutput> {
    return signIn({ username, password });
  }

  static async refreshSession(): Promise<void> {
    await fetchAuthSession({ forceRefresh: true });
  }

  static async signOut(): Promise<void> {
    await signOut({ global: true });
  }

  static async changePassword(password: string): Promise<void> {
    await confirmSignIn({ challengeResponse: password });
  }

  static async initReset(username: string) {
    return resetPassword({ username });
  }

  static async finishReset(
    username: string,
    verificationCode: string,
    password: string
  ): Promise<void> {
    await confirmResetPassword({
      username,
      confirmationCode: verificationCode,
      newPassword: password,
    });
  }

  static async getPrincipal(): Promise<UserPrincipal> {
    const session = await fetchAuthSession();
    const payload = session.tokens?.idToken?.payload;

    if (!payload) throw new Error('No authenticated session');

    const sub = payload.sub;
    const groups = payload['cognito:groups'];
    const email = payload.email;
    const name = payload.name;
    const familyName = payload.family_name;
    const clientId = payload['custom:client_id'];
    const cguAcceptance = payload['custom:cguAcceptance'];

    if (
      typeof sub !== 'string' ||
      typeof email !== 'string' ||
      typeof name !== 'string' ||
      typeof familyName !== 'string' ||
      typeof clientId !== 'string'
    ) {
      throw new Error('Invalid token payload: missing required user claims');
    }

    return {
      username: sub,
      sub,
      groups: Array.isArray(groups) && groups.every((g) => typeof g === 'string') ? groups : [],
      email,
      name,
      familyName,
      clientId,
      cguAcceptance: typeof cguAcceptance === 'string' ? cguAcceptance : undefined,
    };
  }

  static async getIdToken(): Promise<string | undefined> {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  }

  static async signInAndRedirect(params: {
    username: string;
    password: string;
    newPasswordRequiredHandler: () => any;
    router: Router;
    userModule: UserModule;
  }): Promise<void> {
    const { username, password, newPasswordRequiredHandler, router, userModule } = params;
    const result = await CognitoAuth.signInUser(username, password);

    if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      return newPasswordRequiredHandler();
    }

    await userModule.getUserInfo();
    await router.push(userModule.redirectUrl).catch((err) => console.warn(err));
  }
}
